import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Calculator, Download, Calendar, TrendingUp, DollarSign, Users, PiggyBank, Percent } from "lucide-react";

interface CostSavingsCalculatorProps {
  onOpenModal: () => void;
}

// Pricing from Orderly Wellness spreadsheet
const OM_AVG_GLP_PRICE = 113; // Average between Tirz and Sema starter pricing

const scenarios = [
  { name: "Low Enrollment", participation: 3, empPays: 25, companyPays: 1225 },
  { name: "Medium Enrollment", participation: 5, empPays: 62, companyPays: 800 },
  { name: "High Enrollment", participation: 10, empPays: 100, companyPays: 500 },
];

const CostSavingsCalculator = ({ onOpenModal }: CostSavingsCalculatorProps) => {
  const [numberOfEmployees, setNumberOfEmployees] = useState(5000);
  const [selectedScenario, setSelectedScenario] = useState(1); // Default to medium enrollment

  const scenario = scenarios[selectedScenario];

  const calculations = useMemo(() => {
    const scriptsPerMonth = Math.round(numberOfEmployees * (scenario.participation / 100));
    
    // Branded GLP-1 costs (what company pays per employee on program)
    const totalCostToCompanyMonthly = scriptsPerMonth * scenario.companyPays;
    const totalCostToCompanyAnnual = totalCostToCompanyMonthly * 12;
    
    // Orderly Meds / True Wellness costs
    const costUsingOMMonthly = scriptsPerMonth * OM_AVG_GLP_PRICE;
    const costUsingOMAnnual = costUsingOMMonthly * 12;
    
    // Company savings
    const companySavingsMonthly = totalCostToCompanyMonthly - costUsingOMMonthly;
    const companySavingsAnnual = totalCostToCompanyAnnual - costUsingOMAnnual;
    const percentSavings = totalCostToCompanyAnnual > 0 
      ? Math.round((companySavingsAnnual / totalCostToCompanyAnnual) * 100)
      : 0;

    return {
      scriptsPerMonth,
      totalCostToCompanyMonthly,
      totalCostToCompanyAnnual,
      costUsingOMMonthly,
      costUsingOMAnnual,
      companySavingsMonthly,
      companySavingsAnnual,
      percentSavings,
    };
  }, [numberOfEmployees, scenario]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0D1B2A]/10 mb-4">
            <Calculator className="w-8 h-8 text-[#0D1B2A]" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Calculate Your GLP-1 Program Savings
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Compare branded GLP-1 costs vs. True Wellness all-inclusive pricing at ${OM_AVG_GLP_PRICE}/month
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <Card className="border-2 border-gray-100 shadow-xl">
            <CardContent className="p-6 md:p-8">
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Input Section */}
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#0D1B2A]" />
                    Your Organization Details
                  </h3>

                  {/* Scenario Selection */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Select Benefit Scenario</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {scenarios.map((s, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedScenario(idx)}
                          className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                            selectedScenario === idx
                              ? "border-[#0D1B2A] bg-[#0D1B2A]/5 shadow-md"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <p className="text-sm font-bold text-[#0D1B2A]">{s.name}</p>
                          <p className="text-xs text-gray-500 mt-1">{s.participation}% enrollment</p>
                          <div className="mt-2 space-y-0.5">
                            <p className="text-xs text-gray-600">Emp pays: ${s.empPays}/mo</p>
                            <p className="text-xs text-gray-600">Co pays: ${s.companyPays}/mo</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Number of Employees */}
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <label className="text-sm font-medium text-gray-700">Total Employees</label>
                      <span className="text-sm font-semibold text-[#0D1B2A]">{numberOfEmployees.toLocaleString()}</span>
                    </div>
                    <Slider
                      value={[numberOfEmployees]}
                      onValueChange={([value]) => setNumberOfEmployees(value)}
                      min={100}
                      max={100000}
                      step={100}
                      className="py-2"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>100</span>
                      <span>100,000</span>
                    </div>
                  </div>

                  {/* Scenario Details */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Current Scenario Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Employee Co-Pay</p>
                        <p className="text-lg font-bold text-gray-900">${scenario.empPays}/mo</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Company Pays (Branded)</p>
                        <p className="text-lg font-bold text-red-600">${scenario.companyPays}/mo</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Participation Rate</p>
                        <p className="text-lg font-bold text-gray-900">{scenario.participation}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Scripts/Month</p>
                        <p className="text-lg font-bold text-gray-900">{calculations.scriptsPerMonth.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* True Wellness Pricing */}
                  <div className="bg-[#0D1B2A]/5 rounded-lg p-4 border border-[#0D1B2A]/10">
                    <p className="text-sm font-medium text-[#0D1B2A] mb-1">True Wellness GLP-1 Program</p>
                    <p className="text-2xl font-bold text-[#0D1B2A]">${OM_AVG_GLP_PRICE}/month</p>
                    <p className="text-xs text-gray-600 mt-1">Avg price includes Semaglutide & Tirzepatide options</p>
                  </div>
                </div>

                {/* Results Section */}
                <div className="space-y-6">
                  <Card className="border-2 border-[#0D1B2A]/30 bg-gradient-to-br from-[#0D1B2A]/5 to-[#1B3A4B]/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-xl flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-[#0D1B2A]" />
                        Cost Comparison
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Key Metrics */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <p className="text-sm text-gray-600">Total Employees</p>
                          <p className="text-2xl font-bold text-gray-900">{numberOfEmployees.toLocaleString()}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <p className="text-sm text-gray-600">On GLP-1 Program</p>
                          <p className="text-2xl font-bold text-gray-900">{calculations.scriptsPerMonth.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Branded GLP-1 Costs */}
                      <div className="border-t border-[#0D1B2A]/20 pt-4">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <DollarSign className="w-4 h-4" /> Branded GLP-1 (Current)
                        </h4>
                        <div className="space-y-2">
                          <div className="bg-white rounded-lg p-3 shadow-sm flex justify-between">
                            <span className="text-sm text-gray-600">Company Cost per Script</span>
                            <span className="font-semibold">${scenario.companyPays}/mo</span>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm flex justify-between">
                            <span className="text-sm text-gray-600">Monthly Cost to Company</span>
                            <span className="font-semibold">{formatCurrency(calculations.totalCostToCompanyMonthly)}</span>
                          </div>
                          <div className="bg-red-50 rounded-lg p-3 shadow-sm flex justify-between border border-red-200">
                            <span className="text-sm font-medium text-red-800">Annual Cost to Company</span>
                            <span className="font-bold text-red-600">{formatCurrency(calculations.totalCostToCompanyAnnual)}</span>
                          </div>
                        </div>
                      </div>

                      {/* True Wellness Costs */}
                      <div className="border-t border-[#0D1B2A]/20 pt-4">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <PiggyBank className="w-4 h-4" /> True Wellness (${OM_AVG_GLP_PRICE}/mo)
                        </h4>
                        <div className="space-y-2">
                          <div className="bg-white rounded-lg p-3 shadow-sm flex justify-between">
                            <span className="text-sm text-gray-600">Company Cost per Script</span>
                            <span className="font-semibold">${OM_AVG_GLP_PRICE}/mo</span>
                          </div>
                          <div className="bg-white rounded-lg p-3 shadow-sm flex justify-between">
                            <span className="text-sm text-gray-600">Monthly Cost to Company</span>
                            <span className="font-semibold">{formatCurrency(calculations.costUsingOMMonthly)}</span>
                          </div>
                          <div className="bg-green-50 rounded-lg p-3 shadow-sm flex justify-between border border-green-200">
                            <span className="text-sm font-medium text-green-800">Annual Cost to Company</span>
                            <span className="font-bold text-green-600">{formatCurrency(calculations.costUsingOMAnnual)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Savings Summary */}
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg p-6 text-white">
                        <div className="text-center mb-4">
                          <span className="inline-block bg-white/25 backdrop-blur-sm text-white text-sm uppercase tracking-widest font-bold px-4 py-1.5 rounded-full mb-3 border border-white/30 shadow-lg">
                            💰 Company Savings
                          </span>
                          <p className="text-lg font-medium mb-1">Your Annual Savings</p>
                          <p className="text-4xl font-bold">{formatCurrency(calculations.companySavingsAnnual)}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-center">
                          <div className="bg-white/20 rounded-lg p-3">
                            <p className="text-sm">Monthly Savings</p>
                            <p className="text-xl font-bold">{formatCurrency(calculations.companySavingsMonthly)}</p>
                          </div>
                          <div className="bg-white/20 rounded-lg p-3">
                            <p className="text-sm">Percent Savings</p>
                            <p className="text-xl font-bold">{calculations.percentSavings}%</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      onClick={onOpenModal}
                      className="flex-1 bg-[#ffdb4d] text-[#0D1B2A] hover:bg-[#ffdb4d]/90"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Full ROI Report
                    </Button>
                    <Button 
                      onClick={onOpenModal}
                      variant="outline" 
                      className="flex-1 border-[#0D1B2A] text-[#0D1B2A] hover:bg-[#0D1B2A]/10"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule Demo
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default CostSavingsCalculator;
